import React, { useState, useRef, useEffect } from 'react';
import { Search, Loader2, Phone, Link as LinkIcon, MapPin, X } from 'lucide-react';
import { filterCities, cities } from '../lib/cities';

interface RestaurantResult {
  text: string;
  phoneNumber: string | null;
  sources?: string[];
}

export const RestaurantPage: React.FC = () => {
  const [restaurant, setRestaurant] = useState('');
  const [city, setCity] = useState('');
  const [searchedRestaurant, setSearchedRestaurant] = useState('');
  const [searchedCity, setSearchedCity] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<RestaurantResult | null>(null);
  const [loadingSources, setLoadingSources] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [findingMore, setFindingMore] = useState(false);
  const cityInputRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (cityInputRef.current && !cityInputRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
        document.body.style.overflow = 'auto';
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'auto';
    };
  }, []);

  // Handle body scroll when suggestions are shown/hidden
  useEffect(() => {
    if (showSuggestions) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [showSuggestions]);

  const extractPhoneNumber = (text: string): { text: string; phoneNumber: string | null } => {
    // Common phone number patterns
    const phonePattern = /(?:(?:[\+]?\d{1,3}[-.\s]?)?(?:\(\d{1,3}\)|\d{1,3})[-.\s]?\d{1,4}[-.\s]?\d{1,4}[-.\s]?\d{1,9})/;
    const match = text.match(phonePattern);
    
    if (match) {
      const phoneNumber = match[0].replace(/[-.\s]/g, '');
      // Remove the phone number from the text to avoid duplication
      const cleanText = text.replace(phonePattern, '').trim();
      return { text: cleanText, phoneNumber };
    }
    
    return { text, phoneNumber: null };
  };

  const handleCityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCity(value);
    
    if (value.trim()) {
      const filtered = filterCities(value);
      setSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleCitySelect = (selectedCity: string) => {
    setCity(selectedCity);
    setSuggestions([]);
    setShowSuggestions(false);
    document.body.style.overflow = 'auto';
  };

  const isCityValid = (cityValue: string): boolean => {
    return cities.includes(cityValue);
  };

  const handleCityClear = () => {
    setCity('');
    setSuggestions([]);
    setShowSuggestions(false);
    document.body.style.overflow = 'auto';
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSearchedRestaurant(restaurant);
    setSearchedCity(city);
    setError(null);
    setResult(null);

    try {
      const statusResponse = await fetch('https://api.perplexity.ai/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PERPLEXITY_API_KEY}`
        },
        body: JSON.stringify({
          model: "sonar",
          messages: [
            {
              role: "system",
              content: "Your job is to tell the user if the restaurant serves halal food. You MUST ONLY list locations that are EXPLICITLY VERIFIED as halal-certified from official sources. NEVER make assumptions or include unverified locations.\n\nCRITICAL RULES:\n• ONLY include locations that you can verify with 100% certainty from official sources\n• If you cannot verify a location's halal status, DO NOT include it\n• If you're unsure about ANY location, exclude it\n• NEVER rely on outdated or unofficial sources\n• If you can't find current, official verification, say so\n\nFor chain restaurants use this EXACT format:\n[Restaurant Name] serves halal food at these locations:\n\n• [ONLY list locations with CURRENT halal certification, with full addresses]\n\nCertified by: [Official certification body]\n\nIf no locations can be verified with certainty:\n[Restaurant Name] has no verified halal-certified locations in [City].\n\nFor single restaurants:\n[Restaurant Name] [is/is not] halal certified.\n\nCertification: [Current certification details if available]\n\nALWAYS end with '---SOURCES---' followed by ONE official verification URL. DO NOT include any other information or text. DO NOT use different formatting."
            },
            {
              role: "user",
              content: `Does ${restaurant} in ${city} serve halal food? Please ONLY list locations that you can verify with 100% certainty are currently halal-certified. Do not include any locations unless you can verify their current halal status from the internet.`
            }
          ]
        })
      });

      if (!statusResponse.ok) {
        throw new Error('Failed to fetch restaurant status');
      }

      const statusData = await statusResponse.json();
      const [mainContent, sourcesContent] = statusData.choices[0].message.content
        .split('---SOURCES---')
        .map(part => part
        .trim());

      // Process the text to ensure proper line breaks are preserved
      const processedText = mainContent
        .replace(/• /g, '\n• ') // Ensure bullet points are on new lines
        .replace(/\n{3,}/g, '\n\n') // Remove excess line breaks
        .trim();

      const { text, phoneNumber } = extractPhoneNumber(mainContent);

      const sources = sourcesContent
        .split('\n')
        .map(source => source.trim())
        .filter(source => source.startsWith('http'));
      
      setResult({ 
        text: processedText,
        phoneNumber, 
        sources 
      });
    } catch (err) {
      setError('Failed to check restaurant. Please try again.');
      console.error('API Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFindMore = async () => {
    setFindingMore(true);
    setError(null);

    try {
      const statusResponse = await fetch('https://api.perplexity.ai/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PERPLEXITY_API_KEY}`
        },
        body: JSON.stringify({
          model: "sonar-pro",
          messages: [
            {
              role: "system",
              content: "Your job is to find ALL verified halal branches of this restaurant chain in the region. ONLY include locations that are EXPLICITLY VERIFIED as halal-certified from official sources. NEVER make assumptions or include unverified locations.\n\nCRITICAL RULES:\n• ONLY include locations that you can verify with 100% certainty from official sources\n• If you cannot verify a location's halal status, DO NOT include it\n• If you're unsure about ANY location, exclude it\n• NEVER rely on outdated or unofficial sources\n• If you can't find current, official verification, say so\n\nUse this EXACT format:\nHalal-certified branches of [Restaurant Name] in the region:\n\n• [ONLY list locations with CURRENT halal certification, with full addresses]\n\nCertified by: [Official certification body]\n\nALWAYS end with '---SOURCES---' followed by ONE official verification URL. DO NOT include any other information or text. DO NOT use different formatting."
            },
            {
              role: "user",
              content: `List ALL verified halal-certified branches of ${searchedRestaurant} in and around ${searchedCity}. Please ONLY list locations that you can verify with 100% certainty are currently halal-certified.`
            }
          ]
        })
      });

      if (!statusResponse.ok) {
        throw new Error('Failed to fetch additional locations');
      }

      const statusData = await statusResponse.json();
      const [mainContent, sourcesContent] = statusData.choices[0].message.content
        .split('---SOURCES---')
        .map(part => part.trim());

      const processedText = mainContent
        .replace(/• /g, '\n• ')
        .replace(/\n{3,}/g, '\n\n')
        .trim();

      const { text, phoneNumber } = extractPhoneNumber(mainContent);

      const sources = sourcesContent
        .split('\n')
        .map(source => source.trim())
        .filter(source => source.startsWith('http'));
      
      setResult({ 
        text: processedText,
        phoneNumber, 
        sources 
      });
    } catch (err) {
      setError('Failed to find additional locations. Please try again.');
      console.error('API Error:', err);
    } finally {
      setFindingMore(false);
    }
  };

  return (
    <main className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        Check Halal Status of Restaurant
      </h1>

      <form onSubmit={handleSearch} className="space-y-4 mb-8">
        <div className="space-y-4 sm:flex sm:space-y-0 sm:space-x-4">
          <div className="flex-1">
            <label htmlFor="restaurant" className="block text-sm font-medium text-gray-700 mb-1">
              Restaurant Name
            </label>
            <input
              type="text"
              id="restaurant"
              value={restaurant}
              onChange={(e) => setRestaurant(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Enter restaurant name"
              required
            />
          </div>
          <div className="flex-1">
            <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
              City
            </label>
            <div ref={cityInputRef} className="relative">
              <div className="relative">
                <input
                  type="text"
                  id="city"
                  autoComplete="off"
                  value={city}
                  onChange={handleCityChange}
                  onFocus={() => {
                    const filtered = filterCities(city);
                    setSuggestions(filtered);
                    setShowSuggestions(true);
                    document.body.style.overflow = 'hidden';
                  }}
                  className={`w-full pl-10 pr-10 py-2 border ${
                    city && !isCityValid(city) ? 'border-red-300' : 'border-gray-300'
                  } rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500`}
                  placeholder="Enter city, country"
                  required
                />
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                {city && (
                  <button
                    type="button"
                    onClick={handleCityClear}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
              {showSuggestions && (
                <div className="absolute z-10 left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-auto">
                {suggestions.length > 0 ? suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleCitySelect(suggestion)}
                    className="w-full text-left px-4 py-2 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      {suggestion}
                    </div>
                  </button>
                )) : (
                  <div className="px-4 py-3 text-sm text-gray-500">
                    No cities found
                  </div>
                )}
                </div>
              )}
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading || !restaurant || !city || !isCityValid(city)}
          className="w-full sm:w-auto flex items-center justify-center gap-2 bg-emerald-600 text-white py-2 px-6 rounded-lg hover:bg-emerald-700 active:bg-emerald-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Checking...
            </>
          ) : (
            <>
              <Search className="w-5 h-5" />
              Check Halal Status
            </>
          )}
        </button>
      </form>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {result && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            {searchedRestaurant}, {searchedCity}
          </h2>
          <div className="text-gray-700 whitespace-pre-wrap leading-relaxed">{result.text}</div>
          <div className="mt-6 pt-4 border-t border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <LinkIcon className="w-4 h-4" />
              Sources
            </h3>
            {result.sources && result.sources.length > 0 ? (
              <ul className="space-y-1">
                {result.sources.map((source, index) => (
                  <li key={index}>
                    <a
                      href={source}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-emerald-600 hover:text-emerald-700 hover:underline flex items-center gap-1"
                    >
                      {new URL(source).hostname.replace('www.', '')}
                      <LinkIcon className="w-3 h-3" />
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">No verification sources available.</p>
            )}
          </div>
          <div className="mt-6 pt-4 border-t border-gray-100">
            <button
              onClick={handleFindMore}
              disabled={findingMore}
              className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-teal-500 to-emerald-600 text-white py-3 px-6 rounded-md shadow-sm hover:shadow-md hover:translate-y-[-2px] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-emerald-600 disabled:active:scale-100 sm:text-base font-medium tracking-wide"
            >
              {findingMore ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Finding more locations...
                </>
              ) : (
                <>
                  Find more halal {searchedRestaurant} branches
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </main>
  );
};

export default RestaurantPage;